import * as React from 'react';
import { useEffect, useState } from 'react';
import { ApiFieldModel, ApiField, getFieldModels, addFieldModel, updateFieldModel, deleteFieldModel, getFieldsByModel, addField, updateField, deleteField } from '@api/index';
import OptionsEditor from './OptionsEditor'; // Editor for dropdown options

interface FieldModelForm { fieldModelName: string; fieldModelDescription: string; fieldModelType: string; }
interface FieldForm { fieldName: string; fieldDataType: string; fieldLength: number; fieldRequired: boolean; fieldTitle: string; fieldOffset: string; fieldShowInSearch: boolean; fieldShowInDataGrid: boolean; fieldGroup: string; fieldIsUniqueFolderIndex: boolean; fieldIsUniqueDocumentIndex: boolean; fieldControlOrderBy: number; fieldUseAsSortingVar: boolean; options: string[]; }

const emptyModel: FieldModelForm = { fieldModelName: '', fieldModelDescription: '', fieldModelType: '' };
const emptyField: FieldForm = { fieldName: '', fieldDataType: 'X', fieldLength: 0, fieldRequired: false, fieldTitle: '', fieldOffset: '', fieldShowInSearch: false, fieldShowInDataGrid: false, fieldGroup: '', fieldIsUniqueFolderIndex: false, fieldIsUniqueDocumentIndex: false, fieldControlOrderBy: 0, fieldUseAsSortingVar: true, options: [] };

// Restrict datatype options to the four supported values and map to backend codes
const dataTypeOptions = [
  { value: 'text', label: 'Text', code: 'X' },
  { value: 'decimal', label: 'Decimal', code: 'D' },
  { value: 'currency', label: 'Currency', code: 'V' },
  { value: 'datetime', label: 'Date/Time', code: 'T' }
];

// Model type options
const modelTypeOptions = [
  { value: 'Document', label: 'Document' },
  { value: 'Folder', label: 'Folder' }
];

export default function FieldDefinitionsAdmin() {
  const [models, setModels] = useState<ApiFieldModel[]>([]);
  const [selectedModelId, setSelectedModelId] = useState<number | null>(null);
  const [modelEditing, setModelEditing] = useState(false);
  const [modelForm, setModelForm] = useState<FieldModelForm>(emptyModel);
  const [fields, setFields] = useState<ApiField[]>([]);
  const [selectedFieldId, setSelectedFieldId] = useState<number | null>(null);
  const [fieldEditing, setFieldEditing] = useState(false);
  const [fieldForm, setFieldForm] = useState<FieldForm>(emptyField);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function normalizeFieldName(input: string): string {
    const raw = (input ?? '').toString().toLowerCase();
    // Transliterate accented characters to ASCII (e.g. � -> e) and drop combining marks
    const s = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    // Replace non-alphanumeric with underscores
    let n = s.replace(/[^a-z0-9]+/g, '_');
    // Trim underscores
    n = n.replace(/^_+|_+$/g, '');
    return n;
  }

  function setFieldNameNormalized(raw: string) {
    const normalized = normalizeFieldName(raw);
    setFieldForm(p => ({ ...p, fieldName: normalized }));
  }

  function setFieldTitleWithAutofill(raw: string) {
    setFieldForm(p => {
      const nextTitle = raw;
      const nextName = (p.fieldName ?? '').trim();

      // Autofill name if name is empty OR equals the normalized previous title (user hasn't customized it)
      const prevTitleNorm = normalizeFieldName(p.fieldTitle ?? '');
      const shouldAutofill = !nextName || nextName === prevTitleNorm;
      return {
        ...p,
        fieldTitle: nextTitle,
        fieldName: shouldAutofill ? normalizeFieldName(nextTitle) : p.fieldName
      };
    });
  }

  // Field name validation: only letters, digits and underscore; no spaces or special characters.
  const fieldNameRegex = /^[A-Za-z0-9_]+$/;

  useEffect(() => { loadModels(); }, []);
  // Keep the edit form in sync with the selected field after grid reloads.
  useEffect(() => {
    if (selectedFieldId != null) {
      loadSelectedFieldIntoForm(selectedFieldId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, selectedFieldId]);
  async function loadModels() { setLoading(true); setError(null); try { setModels(await getFieldModels()); } catch(e:any){ setError(e.message);} finally { setLoading(false);} }
  async function loadFields(modelId:number){ try { setFields(await getFieldsByModel(modelId)); } catch { setFields([]);} }

  function handleSelectModel(id:number){ setSelectedModelId(id); setModelEditing(false); setSelectedFieldId(null); setFieldEditing(false); loadFields(id); const m=models.find(x=>x.id===id); if(m) setModelForm({ fieldModelName:m.fieldModelName ?? '', fieldModelDescription:m.fieldModelDescription ?? '', fieldModelType:m.fieldModelType ?? '' }); }
  function handleAddModel(){ setSelectedModelId(null); setModelEditing(true); setModelForm(emptyModel); setFields([]); setSelectedFieldId(null); }
  function handleEditModel(){ if(selectedModelId!=null) setModelEditing(true); }
  async function handleDeleteModel(){ if(selectedModelId==null) return; if(!window.confirm('Delete this field model (and its fields)?')) return; setLoading(true); setError(null); try { await deleteFieldModel(selectedModelId); await loadModels(); setSelectedModelId(null); setFields([]);} catch(e:any){ setError(e.message);} finally { setLoading(false);} }
  async function handleSaveModel(){ setLoading(true); setError(null); try { const payload={ fieldModelName:modelForm.fieldModelName, fieldModelDescription:modelForm.fieldModelDescription, fieldModelType:modelForm.fieldModelType } as Omit<ApiFieldModel,'id'>; let id=selectedModelId; if(id==null){ const nm=await addFieldModel(payload); id=nm.id; await loadModels(); } else { await updateFieldModel(id,payload); await loadModels(); } setSelectedModelId(id!); setModelEditing(false); await loadFields(id!);} catch(e:any){ setError(e.message);} finally { setLoading(false);} }

    // Map datatype display name (from backend) to code used in form
    function dataTypeNameToCode(name?: string): string {
        const n = name?.toLowerCase();
        if (!n) return emptyField.fieldDataType;
        if (n === 'text') return 'X';
        if (n === 'decimal' || n === 'number' || n === 'numeric') return 'D';
        if (n === 'currency' || n === 'valuta') return 'V';
        if (n === 'datetime' || n === 'date' || n === 'date/time') return 'T';
        return emptyField.fieldDataType;
    }

  function loadSelectedFieldIntoForm(id: number) {
    const f = fields.find(x => x.id === id);
    if (!f) return;
    const dt = (f.fieldDataType && String(f.fieldDataType).trim()) || dataTypeNameToCode((f as any).fieldDatatypeName);

    const rawUse = (f as any).fieldUseAsSortingVar ?? (f as any).FieldUseAsSortingVar;
    const useAsSorting = rawUse == null ? true : String(rawUse).toLowerCase() !== 'false';

    setFieldForm({
      fieldName: f.fieldName,
      fieldDataType: dt || emptyField.fieldDataType,
      fieldLength: f.fieldLength,
      fieldRequired: f.fieldRequired,
      fieldTitle: f.fieldTitle || '',
      fieldOffset: f.fieldOffset || '',
      fieldShowInSearch: !!f.fieldShowInSearch,
      fieldShowInDataGrid: !!f.fieldShowInDataGrid,
      fieldGroup: (f as any).fieldGroup ?? (f as any).FieldGroup ?? '',
      fieldIsUniqueFolderIndex: !!f.fieldIsUniqueFolderIndex,
      fieldIsUniqueDocumentIndex: !!f.fieldIsUniqueDocumentIndex,
      fieldControlOrderBy: Number((f as any).fieldControlOrderBy ?? 0),
      fieldUseAsSortingVar: useAsSorting,
      options: Array.isArray((f as any).options) ? ([...((f as any).options)] as string[]) : []
    });
  }

  function handleSelectField(id:number){ setSelectedFieldId(id); setFieldEditing(true); loadSelectedFieldIntoForm(id); }
  function handleAddField(){
    const maxExisting = fields
      .map(f => Number((f as any)?.fieldControlOrderBy ?? 0))
      .filter(n => Number.isFinite(n) && n > 0)
      .reduce((max, n) => Math.max(max, n), 0);

    setSelectedFieldId(null);
    setFieldEditing(true);
    setFieldForm({ ...emptyField, fieldControlOrderBy: maxExisting + 1 });
  }
  function handleEditField(){ if(selectedFieldId!=null) { loadSelectedFieldIntoForm(selectedFieldId); setFieldEditing(true); } }

  function handleCancelFieldEdit() {
    if (selectedFieldId != null) {
      // revert any local edits back to the selected field's current values
      loadSelectedFieldIntoForm(selectedFieldId);
      setFieldEditing(false);
    } else {
      setFieldEditing(false);
    }
  }
  async function handleDeleteField(){ if(selectedFieldId==null) return; if(!window.confirm('Delete this field?')) return; setLoading(true); setError(null); try { await deleteField(selectedFieldId); await loadFields(selectedModelId!); setSelectedFieldId(null);} catch(e:any){ setError(e.message);} finally { setLoading(false);} }
  async function handleSaveField(){ 
    if(selectedModelId==null) return; 
    setLoading(true); 
    setError(null); 
    try { 
      // Use code directly as selected value for API
      const apiDataType = fieldForm.fieldDataType; 
      const payload={ 
        fieldModelId:selectedModelId, 
        fieldName:fieldForm.fieldName, 
        fieldDataType: apiDataType, 
        fieldLength:fieldForm.fieldLength, 
        fieldRequired:fieldForm.fieldRequired, 
        fieldTitle:fieldForm.fieldTitle, 
        fieldOffset:fieldForm.fieldOffset, 
        // legacy flags (kept for compatibility)
        fieldShowInSearch:fieldForm.fieldShowInSearch, 
        fieldShowInDataGrid:fieldForm.fieldShowInDataGrid, 
        fieldGroup: fieldForm.fieldGroup,
        fieldIsUniqueFolderIndex:fieldForm.fieldIsUniqueFolderIndex, 
        fieldIsUniqueDocumentIndex:fieldForm.fieldIsUniqueDocumentIndex,
        fieldControlOrderBy: fieldForm.fieldControlOrderBy,
        fieldUseAsSortingVar: fieldForm.fieldUseAsSortingVar ? 'true' : 'false',
        options: fieldForm.options
      } as Omit<ApiField,'id'>; 
      let createdId: number | null = null;
      if(selectedFieldId==null) {
        const created = await addField(payload);
        createdId = (created as any)?.id ?? null;
        setSelectedFieldId(createdId);
      } else {
        await updateField(selectedFieldId,payload);
      }
      await loadFields(selectedModelId);
      // Keep user in edit mode so they can continue editing.
      setFieldEditing(true);
    } catch(e:any){ 
      setError(e.message);
    } finally { 
      setLoading(false);
    } 
  }

  function onModelFormChange(e: React.ChangeEvent<HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement>){ const {name,value}=e.target; setModelForm(p=>({...p,[name]:value})); }
  function onFieldFormChange(e: React.ChangeEvent<HTMLInputElement|HTMLSelectElement>){
    const tgt = e.target as HTMLInputElement;
    const { name, value, type, checked } = tgt;
    setFieldForm(p => ({
      ...p,
      // A single UI checkbox controls both unique flags.
      ...(name === 'fieldIsUnique'
        ? { fieldIsUniqueFolderIndex: checked, fieldIsUniqueDocumentIndex: checked }
        : { [name]: type === 'checkbox' ? checked : (name === 'fieldLength' || name === 'fieldControlOrderBy' ? Number(value) : value) })
    } as any));
  }


  // Helper function to get display label for datatype codes
  function getDataTypeLabel(code: string): string {
    const option = dataTypeOptions.find(opt => opt.code === code);
    return option?.label || code;
  }

  const inputStyle: React.CSSProperties = { padding:'4px 6px', fontSize:14, borderRadius:4, border:'1px solid #cfd8dc', background:'#f7fafd', width:'100%' };
  const buttonPrimary: React.CSSProperties = { padding:'8px 16px', borderRadius:6, background:'#4e6e8e', color:'#fff', fontWeight:600, border:'none', cursor:'pointer' };
  const buttonSecondary: React.CSSProperties = { ...buttonPrimary, background:'#e0e6ed', color:'#123', fontWeight:500 };
  const buttonDanger: React.CSSProperties = { ...buttonPrimary, background:'#c62828' };

  const selectedModel=models.find(m=>m.id===selectedModelId)||null; 
  const selectedField=fields.find(f=>f.id===selectedFieldId)||null;

  const fieldNameTrimmed = (fieldForm.fieldName ?? '').trim();
  const hasFieldNameValue = fieldNameTrimmed.length > 0;
  const isFieldNameValid = !hasFieldNameValue || fieldNameRegex.test(fieldNameTrimmed);
  const fieldNameError = (!isFieldNameValid && hasFieldNameValue) ? "Only letters, digits and underscore are allowed." : null;

  // Sort models by name (then type, then id) for predictable UI ordering.
  const sortedModels = React.useMemo(() => {
    return [...models].sort((a, b) => {
      const byName = (a.fieldModelName ?? '').localeCompare((b.fieldModelName ?? ''), undefined, { sensitivity: 'base' });
      if (byName !== 0) return byName;
      const byType = (a.fieldModelType ?? '').localeCompare((b.fieldModelType ?? ''), undefined, { sensitivity: 'base' });
      if (byType !== 0) return byType;
      return (a.id ?? 0) - (b.id ?? 0);
    });
  }, [models]);

  // Sort fields by fieldControlOrderBy then by name
  const sortedFields = React.useMemo(() => {
    const getOrder = (f: any) => (typeof f.fieldControlOrderBy === 'number' ? f.fieldControlOrderBy : Number(f.fieldControlOrderBy ?? Number.MAX_SAFE_INTEGER));
    return [...fields].sort((a: any, b: any) => {
      const ao = getOrder(a);
      const bo = getOrder(b);
      if (ao !== bo) return (ao ?? Number.MAX_SAFE_INTEGER) - (bo ?? Number.MAX_SAFE_INTEGER);
      return (a.fieldName || '').localeCompare(b.fieldName || '');
    });
  }, [fields]);

  return (
      <div style={{ display: 'flex', height:'calc(90vh / var(--ui-scale))', minHeight:'85vh', border:'1px solid #ccc', borderRadius:12, overflow:'hidden' }}>
      <div style={{ width:380, borderRight:'1px solid #ccc', display:'flex', flexDirection:'column', background:'#f7fafd' }}>
        <div style={{ display:'flex', justifyContent:'space-between', padding:'12px 16px', borderBottom:'1px solid #e0e6ed' }}>
          <h3 style={{ margin:0, fontWeight:600 }}>Field Models</h3>
          <button onClick={handleAddModel} title='Add field model' style={{ background:'#4e6e8e', border:'none', width:36, height:36, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
            <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
              <line x1='12' y1='5' x2='12' y2='19'/>
              <line x1='5' y1='12' x2='19' y2='12'/>
            </svg>
          </button>
        </div>
        {loading && <div style={{ padding:'8px 16px' }}>Loading...</div>}
        {error && <div style={{ padding:'8px 16px', color:'red' }}>{error}</div>}
        <div style={{ overflowY:'auto', flex:1 }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:14 }}>
            <thead>
              <tr style={{ background:'#eaf1f7' }}>
                <th style={{ textAlign:'left', padding:'6px 10px', borderBottom:'1px solid #e0e6ed' }}>Name</th>
                <th style={{ textAlign:'left', padding:'6px 10px', borderBottom:'1px solid #e0e6ed' }}>Type</th>
              </tr>
            </thead>
            <tbody>
              {sortedModels.map(m=>{ 
                const sel=m.id===selectedModelId; 
                return (
                  <tr key={m.id} onClick={()=>handleSelectModel(m.id)} style={{ cursor:'pointer', background: sel? '#d8ecff':'transparent' }}>
                    <td style={{ padding:'6px 10px', borderBottom:'1px solid #f0f4f7' }}>{m.fieldModelName}</td>
                    <td style={{ padding:'6px 10px', borderBottom:'1px solid "#f0f4f7' }}>{m.fieldModelType}</td>
                  </tr>
                ); 
              })}
              {sortedModels.length===0 && !loading && (
                <tr>
                  <td colSpan={2} style={{ padding:16, textAlign:'center', color:'#888' }}>No field models</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div style={{ flex:1, display:'flex', flexDirection:'column', padding:'16px 24px', overflow:'auto' }}>
        {modelEditing ? (
          <div style={{ maxWidth:900 }}>
            <h3 style={{ marginTop:0 }}>{selectedModelId==null? 'Add Field Model':'Edit Field Model'}</h3>
            <form onSubmit={e=>{ e.preventDefault(); handleSaveModel(); }} style={{ display:'grid', gridTemplateColumns:'170px 1fr 170px 1fr', gap:12, background:'#fff', padding:20, borderRadius:12, border:'1px solid #e0e6ed', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
              <label style={{ alignSelf:'center', fontWeight:500 }}>Name</label>
              <input name='fieldModelName' value={modelForm.fieldModelName} onChange={onModelFormChange} required style={inputStyle} />
              <label style={{ alignSelf:'center', fontWeight:500 }}>Type</label>
              <select name='fieldModelType' value={modelForm.fieldModelType} onChange={onModelFormChange} required style={inputStyle}>
                <option value=''>Select type...</option>
                {modelTypeOptions.map(o=> <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <label style={{ alignSelf:'center', fontWeight:500 }}>Description</label>
              <input name='fieldModelDescription' value={modelForm.fieldModelDescription} onChange={onModelFormChange} style={{ ...inputStyle, gridColumn:'span 3' }} />
              <div style={{ gridColumn:'1 / span 4', display:'flex', gap:12, marginTop:4 }}>
                <button type='submit' style={buttonPrimary} disabled={loading}>Save</button>
                <button type='button' style={buttonSecondary} onClick={()=>setModelEditing(false)} disabled={loading}>Cancel</button>
              </div>
            </form>
          </div>
        ) : selectedModel ? (
          <div style={{ display:'flex', flexDirection:'column', gap:24 }}>
            <div style={{ maxWidth:1000 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <h3 style={{ margin:0 }}>Field Model Details</h3>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={handleEditModel} style={buttonSecondary} disabled={loading}>Edit</button>
                  <button onClick={handleDeleteModel} style={buttonDanger} disabled={loading}>Delete</button>
                </div>
              </div>
              <div style={{ background:'#fff', padding:20, borderRadius:12, border:'1px solid #e0e6ed', boxShadow:'0 1px 4px rgba(0,0,0,0.04)', display:'grid', gridTemplateColumns:'160px 1fr', rowGap:8 }}>
                <div style={{ fontWeight:500 }}>Name:</div>
                <div>{selectedModel.fieldModelName}</div>
                <div style={{ fontWeight: 500 }}>Description:</div>
                <div>{selectedModel.fieldModelDescription}</div>
                <div style={{ fontWeight:500 }}>Type:</div>
                <div>{selectedModel.fieldModelType}</div>
              </div>
            </div>
            <div style={{ maxWidth:1400 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <h3 style={{ margin:0 }}>Fields</h3>
                <button onClick={handleAddField} style={buttonSecondary} disabled={loading}>Add Field</button>
              </div>
              <div style={{ border:'1px solid #e0e6ed', borderRadius:12, overflow:'hidden', background:'#fff', maxHeight:'400px' }}>
                <div style={{ overflowY:'auto', maxHeight:'400px' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:14 }}>
                    <thead>
                      <tr style={{ background:'#eaf1f7', position:'sticky', top:0 }}>
                        <th style={{ textAlign:'left', padding:'6px 10px', borderBottom:'1px solid #e0e6ed' }}>Order</th>
                        <th style={{ textAlign:'left', padding:'6px 10px', borderBottom:'1px solid #e0e6ed' }}>Name</th>
                        <th style={{ textAlign:'left', padding:'6px 10px', borderBottom:'1px solid #e0e6ed' }}>Title</th>
                        <th style={{ textAlign:'left', padding:'6px 10px', borderBottom:'1px solid #e0e6ed' }}>Type</th>
                        <th style={{ textAlign:'left', padding:'6px 10px', borderBottom:'1px solid #e0e6ed' }}>Length</th>
                        <th style={{ textAlign:'left', padding:'6px 10px', borderBottom:'1px solid #e0e6ed' }}>Req</th>
                        <th style={{ textAlign:'left', padding:'6px 10px', borderBottom:'1px solid #e0e6ed' }}>Group</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedFields.map((f:any)=>{ 
                        const sel=f.id===selectedFieldId; 
                        const isUnique = !!f.fieldIsUniqueFolderIndex || !!f.fieldIsUniqueDocumentIndex;
                        return (
                          <tr key={f.id} onClick={()=>handleSelectField(f.id)} style={{ cursor:'pointer', background: sel? '#d8ecff':'transparent' }}>
                            <td style={{ padding:'6px 10px', borderBottom:'1px solid #f0f4f7' }}>{f.fieldControlOrderBy}</td>
                            <td style={{ padding:'6px 10px', borderBottom:'1px solid #f0f4f7' }}>
                              {f.fieldName}
                              {isUnique && <span title="Unique index" style={{ marginLeft: 6, color: '#b58800' }}>&#x1F511;</span>}
                            </td>
                            <td style={{ padding:'6px 10px', borderBottom:'1px solid #f0f4f7' }}>{f.fieldTitle}</td>
                            <td style={{ padding:'6px 10px', borderBottom:'1px solid #f0f4f7' }}>{f.fieldDatatypeName}</td>
                            <td style={{ padding:'6px 10px', borderBottom:'1px solid #f0f4f7' }}>{f.fieldLength}</td>
                            <td style={{ padding:'6px 10px', borderBottom:'1px solid #f0f4f7' }}>{f.fieldRequired? 'Yes':'No'}</td>
                            <td style={{ padding:'6px 10px', borderBottom:'1px solid #f0f4f7' }}>{(f.fieldGroup ?? f.FieldGroup) || ''}</td>
                          </tr>
                        ); 
                      })}
                      {sortedFields.length===0 && (
                        <tr>
                          <td colSpan={7} style={{ padding:16, textAlign:'center', color:'#888' }}>No fields</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              {(fieldEditing || selectedFieldId != null) ? (
                <form onSubmit={e=>{ e.preventDefault(); handleSaveField(); }} style={{ marginTop:16, display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, background:'#fff', padding:20, borderRadius:12, border:'1px solid #e0e6ed', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
                  {/* Left column */}
                  <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                    <div style={{ display:'grid', gridTemplateColumns:'150px 1fr', gap:12, alignItems:'start' }}>
                      <label style={{ paddingTop:6, fontWeight:500 }}>Title</label>
                      <input
                        name='fieldTitle'
                        value={fieldForm.fieldTitle}
                        onChange={e => setFieldTitleWithAutofill((e.target as HTMLInputElement).value)}
                        style={inputStyle}
                      />

                      <label style={{ paddingTop:6, fontWeight:500 }}>Name</label>
                      <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                        <input
                          name='fieldName'
                          value={fieldForm.fieldName}
                          onChange={e => setFieldNameNormalized((e.target as HTMLInputElement).value)}
                          required
                          pattern="[A-Za-z0-9_]+"
                          title={fieldNameError ?? undefined}
                          aria-invalid={!!fieldNameError}
                           style={{
                            ...inputStyle,
                            border: fieldNameError ? '1px solid #c62828' : inputStyle.border
                          }}
                        />
                        {!!fieldNameError && (
                          <div style={{ color:'#c62828', fontSize:12 }}>{fieldNameError}</div>
                        )}
                      </div>

                      <label style={{ paddingTop:6, fontWeight:500 }}>Group</label>
                      <input name='fieldGroup' value={fieldForm.fieldGroup} onChange={onFieldFormChange} style={inputStyle} placeholder='Optional group header' />
                    </div>

                    {/* Dropdown options editor for text fields (left column) */}
                    {fieldForm.fieldDataType === 'X' && (
                      <div style={{ borderTop:'1px solid #eef2f6', paddingTop:12, marginTop:4 }}>
                        <div style={{ fontWeight:600, marginBottom:8 }}>Dropdown Options</div>
                        <div style={{ color:'#556', fontSize:12, marginBottom:10 }}>
                          Only for validation input/edit dialogs. Does not affect existing values.
                        </div>
                        <OptionsEditor
                          value={fieldForm.options}
                          onChange={(opts)=>setFieldForm(p=>({ ...p, options: opts }))}
                        />
                      </div>
                    )}
                  </div>

                  {/* Right column */}
                  <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                    <div style={{ display:'grid', gridTemplateColumns:'150px 1fr', gap:12, alignItems:'center' }}>
                      <label style={{ fontWeight:500 }}>Data Type</label>
                      <select name='fieldDataType' value={fieldForm.fieldDataType} onChange={onFieldFormChange} style={{ ...inputStyle, maxWidth: 190 }}>
                        {dataTypeOptions.map(o=> <option key={o.code} value={o.code}>{o.label}</option>)}
                      </select>

                      <label style={{ fontWeight:500 }}>Required</label>
                      <div style={{ display:'flex', alignItems:'center', height:'100%' }}>
                        <input type='checkbox' name='fieldRequired' checked={fieldForm.fieldRequired} onChange={onFieldFormChange} />
                      </div>

                      <label style={{ fontWeight:500 }}>Include in folder description</label>
                      <div style={{ display:'flex', alignItems:'center', height:'100%' }}>
                        <input
                          type='checkbox'
                          name='fieldUseAsSortingVar'
                          checked={!!fieldForm.fieldUseAsSortingVar}
                          onChange={onFieldFormChange}
                          title='When enabled, this field is included when computing the folder display/description.'
                        />
                      </div>

                      <label style={{ fontWeight:500 }}>Unique</label>
                      <div style={{ display:'flex', alignItems:'center', height:'100%' }}>
                        <input
                          type='checkbox'
                          name='fieldIsUnique'
                          checked={!!fieldForm.fieldIsUniqueFolderIndex || !!fieldForm.fieldIsUniqueDocumentIndex}
                          onChange={onFieldFormChange}
                          title='Determines if this field is part of a primary key. Documents with that key will be replaced during import'
                        />
                      </div>

                      <label style={{ fontWeight:500 }}>Order</label>
                      <input name='fieldControlOrderBy' type='number' inputMode='numeric' min={0} max={999} value={fieldForm.fieldControlOrderBy} onChange={onFieldFormChange} style={{ ...inputStyle, maxWidth: 90 }} />

                      <label style={{ fontWeight:500 }}>Length</label>
                      <input name='fieldLength' type='number' inputMode='numeric' min={0} max={9999} value={fieldForm.fieldLength} onChange={onFieldFormChange} style={{ ...inputStyle, maxWidth: 90 }} />
                    </div>
                                      {/*<label style={{ alignSelf:'center', fontWeight:500 }}>Show In Search</label>
                  <input type='checkbox' name='fieldShowInSearch' checked={fieldForm.fieldShowInSearch} onChange={onFieldFormChange} />
                  <label style={{ alignSelf:'center', fontWeight:500 }}>Show In Grid</label>
                  <input type='checkbox' name='fieldShowInDataGrid' checked={fieldForm.fieldShowInDataGrid} onChange={onFieldFormChange} />
                  <label style={{ alignSelf:'center', fontWeight:500 }}>Unique Folder</label>
                  <input type='checkbox' name='fieldIsUniqueFolderIndex' checked={fieldForm.fieldIsUniqueFolderIndex} onChange={onFieldFormChange} />
                  <label style={{ alignSelf:'center', fontWeight:500 }}>Unique Document</label>
                  <input type='checkbox' name='fieldIsUniqueDocumentIndex' checked={fieldForm.fieldIsUniqueDocumentIndex} onChange={onFieldFormChange} />*/}
                  </div>

                  <div style={{ gridColumn:'1 / span 2', display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, marginTop:4 }}>
                    <div style={{ display:'flex', gap:12 }}>
                      <button type='submit' style={buttonPrimary} disabled={loading || !isFieldNameValid}>Save Field</button>
                      <button type='button' style={buttonSecondary} onClick={handleCancelFieldEdit} disabled={loading}>Cancel</button>
                    </div>
                    {selectedFieldId != null && (
                      <button type='button' onClick={handleDeleteField} style={buttonDanger} disabled={loading}>Delete Field</button>
                    )}
                  </div>
                </form>
              ) : (
                <div style={{ marginTop:16, color:'#567' }}>Select a field to view details or click 'Add Field'.</div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ color:'#567', fontSize:16, paddingTop:12 }}>Select a field model or click the + icon to add a new field model.</div>
        )}
      </div>
    </div>
  );
}
